import { deferTracking, sendGAEvent } from '@noths/polaris-client-google-analytics';
import type { EventsMap } from '@noths/polaris-client-next-redux';
import { isBrowser } from '@noths/polaris-client-utils';
import type { Dispatch } from '@reduxjs/toolkit';

import { PAGE_SIZE } from 'src/adapters/storeToServiceRequest/listings-api/adaptStoreToRequestBody';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import {
  PRODUCT_IMPRESSION_BATCH_SIZE,
  PRODUCT_IMPRESSION_TIMEOUT_MS,
} from 'src/redux/products/constants';
import {
  selectCurrentPage,
  selectPreviousPage,
  selectProductFromCode,
  selectProviderAttributionToken,
  selectProviderMetadata,
  selectTotalProducts,
} from 'src/redux/products/selectors/selectors';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { TriggerName } from 'src/redux/products/types';
import type { Product } from 'src/redux/products/types/Product';
import {
  selectActiveFacets,
  selectActiveSortOption,
} from 'src/redux/productsQuery/selectors/selectors';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking/slice';
import { getProductImpressionsTrackingEvent } from 'src/tracking/events/productImpression';
import { getPageTrackingContext } from 'src/tracking/utils/getPageTrackingContext';
import type { ClearEcommerceObject } from 'src/types/googleAnalytics';
import { getImpressionFieldObject } from 'src/utils/googleAnalytics';
import { transformProductsToGAReturnedProductsArray } from 'src/utils/transformProductsToGAReturnedProductsArray';

type RequestProductsAction = ReturnType<(typeof requestProducts)['fulfilled']>;

/** Returns whether an event was sent, so the caller only clears a queue that has gone. */
const sendProductImpressionsEvent = (
  productImpressionQueue: number[],
  state: ReduxApplicationState,
): boolean => {
  const { pages, productsReturnedCount } = state.products;

  const context = getPageTrackingContext(state);

  // A queued code whose product is no longer loaded cannot be described, and an
  // empty field object in the impressions array is worse than a shorter array.
  const queuedProducts = productImpressionQueue
    .map((productCode) => selectProductFromCode(state, productCode))
    .filter((product): product is Product => Boolean(product));

  if (queuedProducts.length === 0) {
    return false;
  }

  sendGAEvent<ClearEcommerceObject>({
    ecommerce: null,
  });

  const productImpressionsEvent = getProductImpressionsTrackingEvent({
    eventCategory: 'Product listings',
    eventAction: 'Product Impressions',
    eventLabel: 'Impressions',
    impressions: queuedProducts.map((product) =>
      getImpressionFieldObject(product, pages, productsReturnedCount, context, {
        listSuffix: product.isExpandedSearch ? ' - expanded' : undefined,
      }),
    ),
  });

  sendGAEvent(productImpressionsEvent);

  return true;
};

/*
  Builds and sends the batch from the state supplied, then clears the queue.

  Which state is supplied matters. `requestProducts.pending` clears
  `products.pages` for every trigger that replaces the results rather than
  appending to them, and the impressions cannot be described without those
  products, so callers must hand in a state from before that clear. See the
  `requestProducts.pending` entry below.
*/
const flushProductImpressionBatch = (state: ReduxApplicationState, dispatch: Dispatch) => {
  const { productImpressionQueue } = state.visibilityTracking;

  if (!isBrowser() || productImpressionQueue.length === 0) {
    return;
  }

  // Only clear a queue that has actually been sent. Clearing unconditionally
  // destroys the impressions instead of giving them another chance next flush.
  if (!sendProductImpressionsEvent(productImpressionQueue, state)) {
    return;
  }

  dispatch(visibilityTrackingActions.clearProductImpressionQueue());
};

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [visibilityTrackingActions.sendProductImpressionBatch.type]: (prevState, _, __, dispatch) => {
    flushProductImpressionBatch(prevState, dispatch);
  },
  /*
    Flush directly from `prevState` instead of dispatching
    `sendProductImpressionBatch`. Handlers run after `next(action)`, so by this
    point the reducer has already cleared `products.pages` for facet, sort, reset
    and page-number changes, and a nested dispatch would re-enter the middleware
    with that cleared state and find nothing to build the event from. `prevState`
    still holds the outgoing page's products.

    On a taxonomy PLP this is the common case, not an edge case: moving between
    taxonomy pages is a shallow route change classified as a facet change, so
    every listing-to-listing move used to discard its impressions. DISC-4541.
  */
  [requestProducts.pending.type]: (prevState, _nextState, _action, dispatch) => {
    flushProductImpressionBatch(prevState, dispatch);
  },
  [visibilityTrackingActions.onProductVisible.type]: (prevState, nextState, _action, dispatch) => {
    const { productImpressionQueue } = nextState.visibilityTracking;

    if (productImpressionQueue.length === PRODUCT_IMPRESSION_BATCH_SIZE) {
      // We have reached the batch limit and should send the batch
      dispatch(visibilityTrackingActions.sendProductImpressionBatch());
      return;
    }

    if (!prevState.visibilityTracking.atLeastOneProductHasBecomeVisible) {
      // Send batch on an interval so we can ensure event is sent during inactivity
      setInterval(
        () => dispatch(visibilityTrackingActions.sendProductImpressionBatch()),
        PRODUCT_IMPRESSION_TIMEOUT_MS,
      );

      // Send batch if user navigates away from page
      window.onbeforeunload = () => {
        dispatch(visibilityTrackingActions.sendProductImpressionBatch());
        return; // Empty return needed to prevent dialog box showing before unload
      };
    }
  },
  [requestProducts.fulfilled.type]: (prevState, nextState, action) => {
    const { payload } = action as RequestProductsAction;
    if (!payload.products) {
      return;
    }

    const {
      products: { products },
      triggerName,
    } = payload;

    const attributionToken = selectProviderAttributionToken(nextState);
    const providerMetadata = selectProviderMetadata(nextState);

    // For filter actions, use products from nextState (filtered results)
    // For other actions, use products from action payload
    const productsToUse =
      triggerName === TriggerName.CHANGE_FACETS ? nextState.products.pages[1] || [] : products;
    const offsetCurrentPage = (selectCurrentPage(nextState) - 1) * PAGE_SIZE;
    const offsetPreviousPage = selectPreviousPage(nextState) * PAGE_SIZE;
    const returnedProducts = productsToUse
      ? transformProductsToGAReturnedProductsArray(productsToUse)
      : null;

    // Common tracking properties for filter events
    const commonFilterEventProps = {
      event: 'custom_event_v2',
      content_type: 'filters',
      offset: offsetCurrentPage,
      ...(attributionToken && { attribution_token: attributionToken }),
      ...(providerMetadata && { crs_filter: providerMetadata.filter }),
      ...(returnedProducts && { returned_products: returnedProducts }),
      total_listing_items: selectTotalProducts(nextState),
    };

    const getModalDetails = (action: string) => ({
      name: 'plp_filter_facet',
      action,
      page_type: 'plp',
    });

    const getFiltersApplied = () =>
      selectActiveFacets(nextState)
        .map((facet) => facet.title)
        .join(', ');

    switch (triggerName) {
      case TriggerName.LOAD_MORE_PRODUCTS:
      case TriggerName.LOAD_PREVIOUS_PRODUCTS: {
        const isLoadPrevious = triggerName === TriggerName.LOAD_PREVIOUS_PRODUCTS;
        const productImpressionsEvent = getProductImpressionsTrackingEvent({
          eventAction: isLoadPrevious ? 'Load Previous Impressions' : 'Load More Impressions',
          offset: isLoadPrevious ? offsetPreviousPage : offsetCurrentPage,
          eventCategory: 'Product listings',
          eventLabel: String(selectCurrentPage(prevState)),
          ...(attributionToken && { attributionToken }),
          ...(providerMetadata && { crsFilter: providerMetadata.filter }),
          ...(returnedProducts && { returnedProducts: returnedProducts }),
        });

        sendGAEvent(productImpressionsEvent);
        break;
      }
      case TriggerName.CHANGE_FACETS: {
        if (payload.metadata) {
          const { facetTitle, optionActive, optionTitle } = payload.metadata;

          // Defer off the critical path: fire-and-forget, runs in the same task as the filtered-results re-render.
          deferTracking(() =>
            sendGAEvent({
              ...commonFilterEventProps,
              event_name: 'select_filter',
              event_details: optionActive ? 'select filter' : 'de-select filter',
              field_name: facetTitle,
              field_value: optionTitle,
              filters_applied: getFiltersApplied(),
              feature_details: getModalDetails('select facet'),
            }),
          );
        }
        break;
      }
      case TriggerName.CHANGE_SORT: {
        const activeSortOption = selectActiveSortOption(nextState);

        // Defer off the critical path: fire-and-forget, runs in the same task as the re-sorted results re-render.
        deferTracking(() =>
          sendGAEvent({
            ...commonFilterEventProps,
            event_name: 'select_filter',
            event_details: 'select filter',
            field_name: 'sort by',
            field_value: activeSortOption?.title,
            filters_applied: '',
            feature_details: getModalDetails('select facet'),
          }),
        );
        break;
      }
      case TriggerName.RESET_FACET: {
        if (payload.metadata) {
          const { facetTitle, optionTitle } = payload.metadata;

          // Defer off the critical path: fire-and-forget, runs in the same task as the results re-render.
          deferTracking(() =>
            sendGAEvent({
              ...commonFilterEventProps,
              event_name: 'filter_interaction',
              event_details: 'reset',
              field_name: facetTitle,
              field_value: optionTitle,
              filters_applied: getFiltersApplied(),
              feature_details: getModalDetails('reset facet'),
            }),
          );
        }
        break;
      }
      case TriggerName.RESET_ALL_FACETS_AND_SORT: {
        // Defer off the critical path: fire-and-forget, runs in the same task as the results re-render.
        deferTracking(() =>
          sendGAEvent({
            ...commonFilterEventProps,
            event_name: 'filter_interaction',
            event_details: 'reset all filters',
            feature_details: getModalDetails('reset all facets'),
          }),
        );

        break;
      }
    }
  },
};
